package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.PapiBlockAst
import top.wcpe.allincore.dsl.compiler.ast.PapiPlaceholderAst
import top.wcpe.allincore.dsl.compiler.lex.Token
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/** 解析 screen 级 `papi { short_name { ... } }` 块。 */
internal class PapiBlockParser(private val p: Parser) {

    fun parse(): PapiBlockAst {
        val start = p.expect(TokenType.KW_PAPI, "papi 块缺少关键字")
        p.expect(TokenType.LBRACE, "papi 后缺少 '{'")
        skipSeparators()
        val placeholders = mutableListOf<PapiPlaceholderAst>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            placeholders += parsePlaceholder()
            if (p.check(TokenType.RBRACE) || p.check(TokenType.EOF)) break
            expectSeparator("papi 条目后缺少换行或 ';'")
            skipSeparators()
        }
        val close = p.expect(TokenType.RBRACE, "papi 块缺少 '}'")
        return PapiBlockAst(placeholders, p.completePosition(start.position, close))
    }

    private fun parsePlaceholder(): PapiPlaceholderAst {
        val nameTok = parseShortNameToken()
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "papi short_name 后缺少 '{'")
        skipSeparators()

        var refreshMs: Long? = null
        var bindState: String? = null
        var bindExpr: ExprAst? = null
        var pipe: ExprAst.LambdaExpr? = null
        var pipeExpr: ExprAst? = null
        var onChange: String? = null
        var onChangeExpr: ExprAst? = null

        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val prop = p.expect(TokenType.IDENTIFIER, "papi 属性名必须是标识符")
            p.skipNewlines()
            p.expect(TokenType.EQUALS, "papi 属性 '${prop.lexeme}' 缺少 '='")
            p.skipNewlines()
            val expr = ActionBlockParser(p).expressionParser(allowBareIdent = true).parse()
            when (prop.lexeme) {
                "refreshMs" -> refreshMs = parseRefreshMs(expr, prop)
                "bind" -> {
                    bindExpr = expr
                    bindState = (expr as? ExprAst.StateRef)?.name
                }
                "pipe" -> {
                    pipeExpr = expr
                    pipe = expr as? ExprAst.LambdaExpr
                }
                "onChange" -> {
                    onChangeExpr = expr
                    onChange = (expr as? ExprAst.FunRefExpr)?.name
                }
                else -> p.fail("papi 不支持属性 '${prop.lexeme}'", prop.position)
            }
            if (p.check(TokenType.RBRACE) || p.check(TokenType.EOF)) break
            expectSeparator("papi 属性后缺少换行或 ';'")
            skipSeparators()
        }
        val close = p.expect(TokenType.RBRACE, "papi short_name 块缺少 '}'")
        return PapiPlaceholderAst(
            shortName = nameTok.lexeme,
            shortNameIsIdentifier = nameTok.type == TokenType.IDENTIFIER,
            refreshMs = refreshMs,
            bindState = bindState,
            bindExpr = bindExpr,
            pipe = pipe,
            pipeExpr = pipeExpr,
            onChange = onChange,
            onChangeExpr = onChangeExpr,
            position = p.completePosition(nameTok.position, close),
        )
    }

    private fun parseShortNameToken(): Token {
        val cur = p.current()
        if (cur.type == TokenType.IDENTIFIER || cur.type == TokenType.STRING || cur.type == TokenType.NUMBER) {
            p.advance()
            return cur
        }
        p.fail("papi short_name 缺失", cur.position)
    }

    private fun parseRefreshMs(expr: ExprAst, prop: Token): Long {
        val number = (expr as? ExprAst.Literal.NumberLit)?.value?.toDouble()
            ?: p.fail("refreshMs 必须是数字字面量", prop.position)
        if (number % 1.0 != 0.0) p.fail("refreshMs 必须是整数毫秒", expr.position)
        return number.toLong()
    }

    private fun skipSeparators() {
        while (p.check(TokenType.NEWLINE) || p.check(TokenType.SEMICOLON)) p.advance()
    }

    private fun expectSeparator(message: String) {
        if (!p.check(TokenType.NEWLINE) && !p.check(TokenType.SEMICOLON)) {
            p.fail(message, p.current().position)
        }
    }
}
